import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { useT } from "@/lib/i18n";
import { endsLabel, formatGal, formatToEighths } from "@/lib/tank";
import { FINGERPRINTS_CHECKED, buildChart, stickNeed, thermalKind } from "@/lib/zones";
import { ManifoldNote, PitchTable, ThermalTable, useChartTools } from "@/components/chart/FieldTools";
import { ZoneLegend } from "@/components/chart/ChartTable";

const ZONE_T = { belly: "Belly", curve: "Curve", orange: "90%+", red: "95%+", none: "" };

function Rows({ rows, t }) {
  return (
    <table className="print-table" data-testid="print-chart">
      <thead><tr><th>{t("Stick")}</th><th>{t("Gallons")}</th><th>{t("90% room")}</th><th>{t("To 90%")}</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.h} className={`pz-${r.zone || "none"}`}>
          <td>{Number.isInteger(r.h) ? `${r.h}"` : formatToEighths(r.h)}</td><td>{formatGal(r.vol)}</td>
          <td>{r.room90 >= 0 ? formatGal(r.room90) : `0 (${t("over by {g}", { g: formatGal(-r.room90) })})`}</td><td>{r.to90 > 0 ? formatToEighths(r.to90) : (ZONE_T[r.zone] && t(ZONE_T[r.zone])) || "—"}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

const FpTable = ({ t }) => (
  <table className="print-table print-fp" data-testid="print-fingerprints">
    <thead><tr><th>{t("Tank")}</th><th>{t("True ID")}</th><th>{t("Max")}</th><th>{t("90% fill")}</th><th>{t("Belly GPI")}</th><th>{t("Stick")}</th></tr></thead>
    <tbody>{FINGERPRINTS_CHECKED.map((f) => (
      <tr key={f.id}><td>{t(f.name)}{f.flags.length ? " ⚑" : ""}</td><td>{f.idLabel}</td><td>{formatGal(f.max)}</td>
        <td>{formatGal(f.max * 0.9)} · {formatToEighths(f.calcH90)}</td><td>{Math.round(f.calcGpi)}</td><td>{f.stick}'</td></tr>
    ))}</tbody>
  </table>
);

export default function ChartPrintPage() {
  const { session: s } = useSession();
  const [tools] = useChartTools();
  const t = useT();
  const nav = useNavigate();
  const chart = useMemo(() => buildChart(s.capacity, s.diameter, s.ends, 0.25), [s.capacity, s.diameter, s.ends]);
  const need = stickNeed(tools.bury, s.diameter);
  return (
    <div className="chart-sheet" data-testid="chart-print-screen">
      <div className="print-actions">
        <button type="button" className="ghost-btn" onClick={() => nav("/chart")} data-testid="print-back-btn"><ArrowLeft size={16} />{t("Back")}</button>
        <button type="button" className="primary-btn" onClick={() => window.print()} data-testid="print-btn"><Printer size={16} />{t("Print / save PDF")}</button>
      </div>
      <h1 className="print-title">{formatGal(s.capacity)} gal · {s.diameter}" · {t(endsLabel(s.ends))}</h1>
      <div className="print-sub">{t("90% shutoff {a} ({g} gal) · 95% max {b} ({h} gal) · Stick needed {n}", {
        a: formatToEighths(chart.h90), g: formatGal(chart.cap90), b: formatToEighths(chart.h95), h: formatGal(chart.cap95), n: formatToEighths(need.need) })}</div>
      <ZoneLegend t={t} />
      <Rows rows={chart.rows} t={t} />
      <h2 className="print-h2">{t("Mystery tank fingerprints")}</h2>
      <FpTable t={t} />
      <div className="print-note">{t("⚑ = your reference numbers differ from shape math. Trust the store's certified chart.")}</div>
      <h2 className="print-h2">{t("Tank tilt correction")}</h2>
      <PitchTable t={t} />
      <h2 className="print-h2">{t("Thermal shrink after the drop")}</h2>
      <ThermalTable gal={s.dropGallons || 0} rackF={tools.rackF} tankF={tools.tankF} kind={thermalKind(s.product)} t={t} />
      <h2 className="print-h2">{t("Manifolded tanks")}</h2>
      <ManifoldNote t={t} />
      <div className="print-note">{t("Estimate from a standard tank shape. The store's tank chart and the console come first.")}</div>
    </div>
  );
}
